/**
 * ManifestEngine — matrix-table-manifest.json ↔ 테이블 구성 합성 다리 (M2)
 */

import { logger } from '@shared/logger';
import type {
  ManifestComposedColumn,
  ManifestComposedGroup,
  ManifestSchemaColumn,
  ManifestUserConfigHints,
} from '@shared/types/manifest-compose';
import { MATRIX_RELATION_TYPE_TO_GROUP } from '@shared/types/matrix-relation';
import type { TableManifest, TableManifestSubdomain } from '@shared/types/table-manifest';
import {
  getManifestForSubdomain,
  loadTableManifest,
  type ManifestFieldGroup,
} from '@shared/utils/loadTableManifest';

const GROUP_META: Record<
  ManifestFieldGroup,
  { label: string; icon: string; color: string; description: string; collapsible: boolean }
> = {
  basic: {
    label: '기본 정보',
    icon: '📋',
    color: 'gray',
    description: 'UniversalBaseEntity 기본 필드',
    collapsible: false,
  },
  custom: {
    label: 'Custom Fields',
    icon: '🏷️',
    color: 'blue',
    description: '도메인별 특화 필드',
    collapsible: true,
  },
  value: {
    label: 'Value Fields',
    icon: '💎',
    color: 'green',
    description: 'ValueField 6요소 순환 체계',
    collapsible: true,
  },
  impact: {
    label: 'Impact Fields',
    icon: '⚡',
    color: 'orange',
    description: 'Flow 실행 시 영향 관계',
    collapsible: true,
  },
  relation: {
    label: 'Relation Fields',
    icon: '🔗',
    color: 'purple',
    description: '도메인 간 참조 관계',
    collapsible: true,
  },
  system: {
    label: 'System Fields',
    icon: '⚙️',
    color: 'gray',
    description: '시스템 메타데이터',
    collapsible: true,
  },
  finance_link: {
    label: 'Finance Links',
    icon: '💰',
    color: 'amber',
    description: '재무 연동 컬럼 (F)',
    collapsible: true,
  },
  reference_link: {
    label: 'Reference Links',
    icon: '🔗',
    color: 'indigo',
    description: '참조 연결 컬럼 (R)',
    collapsible: true,
  },
  trigger_link: {
    label: 'Trigger Links',
    icon: '⏰',
    color: 'teal',
    description: '트리거 자동화 컬럼 (T)',
    collapsible: true,
  },
  data_mirror: {
    label: 'Data Mirrors',
    icon: '🪞',
    color: 'cyan',
    description: 'ImpactField 미러 컬럼 (D)',
    collapsible: true,
  },
  state_signal: {
    label: 'State Signals',
    icon: '🧠',
    color: 'violet',
    description: '감정·상태 신호 컬럼 (E)',
    collapsible: true,
  },
};

const DOMAIN_COLUMN_DEFS: Record<
  string,
  Pick<ManifestComposedColumn, 'key' | 'label' | 'fieldGroup' | 'type' | 'priority'>
> = {
  permissionMatrix: {
    key: 'permissions',
    label: '권한',
    fieldGroup: 'basic',
    type: 'status',
    priority: 9,
  },
  userConnection: {
    key: 'linkedUser',
    label: '연결된 사용자',
    fieldGroup: 'basic',
    type: 'reference',
    priority: 8,
  },
};

export class ManifestEngine {
  static isEnabled(): boolean {
    return process.env.ENABLE_MANIFEST_ENGINE === 'true';
  }

  static shouldUseManifest(subdomain?: string): boolean {
    return Boolean(
      subdomain && ManifestEngine.isEnabled() && ManifestEngine.hasManifest(subdomain)
    );
  }

  static hasManifest(subdomain: string): boolean {
    return getManifestForSubdomain(subdomain) !== undefined;
  }

  static getEntry(
    subdomain: string,
    manifest: TableManifest = loadTableManifest()
  ): TableManifestSubdomain | undefined {
    return manifest.subdomains.find((entry) => entry.subdomain === subdomain);
  }

  static getDefaultVisible(subdomain: string): string[] {
    return getManifestForSubdomain(subdomain)?.defaultVisible ?? [];
  }

  static getRowActions(subdomain: string): string[] {
    return getManifestForSubdomain(subdomain)?.rowActions ?? [];
  }

  static getWorkflows(subdomain: string): string[] {
    return getManifestForSubdomain(subdomain)?.workflows ?? [];
  }

  static getDomainColumns(subdomain: string): string[] {
    return getManifestForSubdomain(subdomain)?.domainColumns ?? [];
  }

  static getGroupOrder(subdomain: string): ManifestFieldGroup[] {
    return getManifestForSubdomain(subdomain)?.groupOrder ?? [];
  }

  static getCollapsedGroups(subdomain: string): ManifestFieldGroup[] {
    const entry = getManifestForSubdomain(subdomain);
    if (!entry) {
      return [];
    }
    const active = new Set(entry.columnGroups);
    return entry.groupOrder.filter((group) => !active.has(group));
  }

  static buildUserConfigHints(subdomain: string): ManifestUserConfigHints | undefined {
    const entry = getManifestForSubdomain(subdomain);
    if (!entry) {
      return undefined;
    }

    const visibleColumns = [...entry.defaultVisible];
    const domainColumnKeys = entry.domainColumns
      .map((name) => DOMAIN_COLUMN_DEFS[name]?.key)
      .filter((key): key is string => Boolean(key));

    for (const key of domainColumnKeys) {
      if (!visibleColumns.includes(key)) {
        visibleColumns.push(key);
      }
    }

    return {
      visibleColumns,
      columnOrder: visibleColumns,
      groupOrder: entry.groupOrder,
      collapsedGroups: ManifestEngine.getCollapsedGroups(subdomain),
    };
  }

  /**
   * SchemaEngine/DynamicTable ColumnConfig 또는 DomainTableColumn을 flat 입력으로 합성
   */
  static compose(
    subdomain: string,
    schemaColumns: ManifestSchemaColumn[],
    manifest: TableManifest = loadTableManifest()
  ): ManifestComposedGroup[] {
    const entry = ManifestEngine.getEntry(subdomain, manifest);
    if (!entry) {
      logger.warn(
        `[ManifestEngine] manifest entry not found for subdomain="${subdomain}" — fallback to legacy preset`
      );
      return [];
    }

    const defaultVisible = new Set(entry.defaultVisible);
    const columnsByGroup = new Map<ManifestFieldGroup, ManifestComposedColumn[]>();

    const pushColumn = (group: ManifestFieldGroup, column: ManifestComposedColumn): void => {
      const bucket = columnsByGroup.get(group) ?? [];
      bucket.push(column);
      columnsByGroup.set(group, bucket);
    };

    for (const column of schemaColumns) {
      pushColumn(column.fieldGroup, ManifestEngine.toComposedColumn(column, defaultVisible));
    }

    for (const hint of entry.relationHints) {
      const groupType = MATRIX_RELATION_TYPE_TO_GROUP[hint.type];
      if (!entry.columnGroups.includes(groupType)) {
        continue;
      }
      pushColumn(groupType, {
        key: `hint_${hint.fieldKey}`,
        label: hint.fieldKey,
        fieldGroup: groupType,
        visible: defaultVisible.has(hint.fieldKey),
        priority: 5,
        type: 'reference',
        sortable: false,
        filterable: false,
        description: `${hint.target} (${hint.type})`,
      });
    }

    for (const domainColumnName of entry.domainColumns) {
      const def = DOMAIN_COLUMN_DEFS[domainColumnName];
      if (!def) {
        logger.warn(
          `[ManifestEngine] unknown domainColumns entry "${domainColumnName}" for subdomain="${subdomain}"`
        );
        continue;
      }
      pushColumn(def.fieldGroup, {
        ...def,
        visible: defaultVisible.has(def.key),
        sortable: false,
        filterable: false,
      });
    }

    const composed: ManifestComposedGroup[] = [];

    for (const groupType of entry.groupOrder) {
      const columns = columnsByGroup.get(groupType);
      if (!columns || columns.length === 0) {
        continue;
      }

      const meta = GROUP_META[groupType];
      const groupActive = entry.columnGroups.includes(groupType);

      composed.push({
        type: groupType,
        label: meta.label,
        icon: meta.icon,
        color: meta.color,
        description: meta.description,
        columns: columns.sort((a, b) => b.priority - a.priority),
        defaultVisible: groupActive,
        collapsible: meta.collapsible,
      });
    }

    return composed;
  }

  static mapDynamicTableFieldType(fieldType?: string): ManifestFieldGroup {
    switch (fieldType) {
      case 'core':
        return 'basic';
      case 'custom':
        return 'custom';
      case 'value':
        return 'value';
      case 'impact':
        return 'impact';
      case 'relation':
        return 'relation';
      default:
        return 'basic';
    }
  }

  private static toComposedColumn(
    column: ManifestSchemaColumn,
    defaultVisible: Set<string>
  ): ManifestComposedColumn {
    return {
      key: column.key,
      label: column.label ?? column.key,
      fieldGroup: column.fieldGroup,
      visible: defaultVisible.has(column.key),
      priority: column.priority ?? 5,
      type: column.type ?? 'text',
      sortable: column.sortable ?? true,
      filterable: column.filterable ?? false,
      ...(column.width !== undefined ? { width: column.width } : {}),
      ...(column.description !== undefined ? { description: column.description } : {}),
    };
  }
}
